import { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { SectionCard, SectionTitle, SubmitButton, CancelButton, ButtonsRow } from './Formstyle';
import { TextField, Stack, IconButton, InputAdornment } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

const validationSchema = Yup.object().shape({
    name: Yup.string().required('Campo obrigatório'),
    username: Yup.string().required('Campo obrigatório'),
    email: Yup.string().email('Digite um e-mail valido').required('Campo obrigatório'),
    phone: Yup.string()
        .required('Campo obrigatório')
        .matches(/^\(\d{2}\) \d{5}-\d{4}$/, 'Use o formato (XX) XXXXX-XXXX'),
    password: Yup.string()
        .test('len', 'Mínimo 6 caracteres', v => !v || v.length >= 6),
    confirm_password: Yup.string()
        .test('match', 'Senhas não coincidem', function (v) {
            return !this.parent.password || this.parent.password === v;
        }),
});

const formatPhone = (phone) => {
    if (!phone) return '';
    let d = phone.replace(/\D/g, '');
    if (d.length === 13 && d.startsWith('55')) d = d.slice(2);
    if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
    return phone;
};

const AccountInfo = ({ user, submit }) => {
    const [editing, setEditing] = useState(false);
    const [showPass, setShowPass] = useState(false);

    const formik = useFormik({
        initialValues: {
            name: user?.name || '',
            username: user?.username || '',
            email: user?.email || '',
            phone: formatPhone(user?.phone),
            password: '',
            confirm_password: '',
        },
        validationSchema,
        onSubmit: (values) => {
            const data = { name: values.name, username: values.username, email: values.email, phone: values.phone };
            if (values.password) data.password = values.password;
            submit(data);
            setEditing(false);
        },
    });

    useEffect(() => {
        if (user) {
            formik.setValues({
                name: user.name || '',
                username: user.username || '',
                email: user.email || '',
                phone: formatPhone(user.phone),
                password: '',
                confirm_password: '',
            });
        }
    }, [user]);

    const handleCancel = () => {
        formik.resetForm();
        setEditing(false);
    };

    const handleFieldClick = () => {
        if (!editing) setEditing(true);
    };

    return (
        <SectionCard>
            <SectionTitle>Dados Pessoais</SectionTitle>
            <Stack spacing={2}>
                <TextField
                    label="Nome completo"
                    name="name"
                    size="small"
                    fullWidth
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    onFocus={handleFieldClick}
                    disabled={!editing}
                    error={formik.touched.name && Boolean(formik.errors.name)}
                    helperText={formik.touched.name && formik.errors.name}
                />
                <TextField
                    label="Nome de usuário"
                    name="username"
                    size="small"
                    fullWidth
                    value={formik.values.username}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    onFocus={handleFieldClick}
                    disabled={!editing}
                    error={formik.touched.username && Boolean(formik.errors.username)}
                    helperText={formik.touched.username && formik.errors.username}
                />
                <TextField
                    label="E-mail"
                    name="email"
                    size="small"
                    fullWidth
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    onFocus={handleFieldClick}
                    disabled={!editing}
                    error={formik.touched.email && Boolean(formik.errors.email)}
                    helperText={formik.touched.email && formik.errors.email}
                />
                <TextField
                    label="Telefone (WhatsApp)"
                    name="phone"
                    size="small"
                    fullWidth
                    placeholder="(XX) XXXXX-XXXX"
                    value={formik.values.phone}
                    onChange={(e) => {
                        let v = e.target.value.replace(/\D/g, '');
                        if (v.length <= 11) {
                            v = v.replace(/^(\d{2})(\d)/g, '($1) $2');
                            v = v.replace(/(\d{5})(\d)/, '$1-$2');
                        }
                        formik.setFieldValue('phone', v);
                    }}
                    onBlur={formik.handleBlur}
                    onFocus={handleFieldClick}
                    disabled={!editing}
                    inputProps={{ maxLength: 15 }}
                    error={formik.touched.phone && Boolean(formik.errors.phone)}
                    helperText={formik.touched.phone && formik.errors.phone}
                />
                {editing && (
                    <>
                        <TextField
                            label="Nova senha"
                            name="password"
                            type={showPass ? 'text' : 'password'}
                            size="small"
                            fullWidth
                            value={formik.values.password}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.password && Boolean(formik.errors.password)}
                            helperText={formik.touched.password ? formik.errors.password : 'Deixe em branco para manter a atual'}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setShowPass(!showPass)}>
                                            {showPass ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        {formik.values.password && (
                            <TextField
                                label="Confirmar nova senha"
                                name="confirm_password"
                                type="password"
                                size="small"
                                fullWidth
                                value={formik.values.confirm_password}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.confirm_password && Boolean(formik.errors.confirm_password)}
                                helperText={formik.touched.confirm_password && formik.errors.confirm_password}
                            />
                        )}
                    </>
                )}
            </Stack>
            <ButtonsRow>
                <CancelButton type="button" $visible={editing} onClick={handleCancel}>
                    Cancelar
                </CancelButton>
                <SubmitButton type="button" $visible={editing} onClick={formik.handleSubmit}>
                    Salvar
                </SubmitButton>
            </ButtonsRow>
            {!editing && (
                <SubmitButton
                    type="button"
                    $visible={true}
                    onClick={() => setEditing(true)}
                    style={{ marginTop: 12, background: 'transparent', color: '#006a61', border: '1.5px solid #006a61' }}
                >
                    Editar dados
                </SubmitButton>
            )}
        </SectionCard>
    );
};

export default AccountInfo;
